import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Reusable confirmation dialog built on top of AlertDialog.
 *
 * Props:
 *  open           – boolean, controls visibility
 *  onOpenChange   – (open: boolean) => void
 *  title          – dialog heading (default: "Are you sure?")
 *  description    – body text / ReactNode
 *  confirmLabel   – text on the confirm button (default: "Confirm")
 *  cancelLabel    – text on the cancel button (default: "Cancel")
 *  loading        – boolean, shows spinner and disables buttons while true
 *  variant        – "danger" | "warning" | "default"  (controls confirm button colour)
 *  onConfirm      – async / sync callback executed when confirm is clicked
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title = "Are you sure?",
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  variant = "danger",
  onConfirm,
}) {
  const confirmClass = {
    danger:  "bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500",
    warning: "bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-400",
    default: "bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-500",
  }[variant] ?? "bg-rose-600 text-white hover:bg-rose-700";

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange?.(o); }}>
      <AlertDialogContent className="rounded-[24px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-black">{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription asChild={typeof description !== "string"}>
              {typeof description === "string" ? (
                description
              ) : (
                <div className="text-sm text-slate-600">{description}</div>
              )}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(confirmClass)}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Please wait…
              </>
            ) : (
              confirmLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
